/* eslint no-underscore-dangle: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import FileFormat from '../FileFormat';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import PopupBox from '../ContentUploadConfirmation';

function HtmlTemplateEightteen({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [openContentUploadConfirmation, setOpenContentUploadConfirmation] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);

  const cssStyles = `
    body {
      margin: 0;
      padding: 0;
    }
    .boutique {
      font-family: "Playfair Display", serif;
      color: #333;
      position: relative;
    }
    .boutique__content {
      max-width: max-content;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    .boutique .main-container {
      padding: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
    .boutique__parent-logo {
      width: 100%;
    }
    .boutique__logo {
      width: 40px;
    }
    .boutique__header {
      text-align: center;
      margin-bottom: 20px;
    }
    .boutique__title-your {
      font-family: "Great Vibes", cursive;
      font-size: 40px;
      color: #7e7d6b;
      display: block;
    }
    .boutique__title-name {
      font-size: 60px;
      color: #2d2d2d;
    }
    .boutique__image-container {
      position: relative;
      display: flex;
      justify-content: center;
    }
    .boutique__image-container .boutique__arch {
      overflow: hidden;
      display: flex;
      width: max-content;
      justify-content: center;
      align-items: center;
    }
    .boutique__image-container .boutique__arch img {
      border-radius: 100% 100% 0 0;
      width: 400px;
      height: 500px;
      object-fit: cover;
    }
    .boutique__image-container .boutique__discount-badge {
      position: absolute;
      inset-inline-end: -30px;
      top: 150px;
      background-color: #9c9b88;
      color: white;
      padding: 18px;
      border-radius: 50%;
      width: 100px;
      height: 100px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 20px;
    }
    .boutique__image-container .boutique__discount-badge .discount-firstText {
      font-size: 14px;
      padding-inline-start: 5px;
    }
    .boutique__image-container .boutique__discount-badge .discount-offer {
      font-size: 40px;
    }
    .boutique__image-container .boutique__discount-badge .discount-lastText {
      text-align: end;
      font-size: 14px;
      padding-inline-end: 10px;
    }
    .boutique__special-offer {
      background-color: #9c9b88;
      color: white;
      font-size: 35px;
      border-radius: 10px;
      padding: 8px 30px;
      position: absolute;
      inset-inline-start: 50%;
      inset-block-end: 10px;
      transform: translateX(-50%);
      width: max-content;
    }
    .boutique__signs {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 15px;
      font-size: 14px;
      color: #7e7d6b;
      margin-block: 30px 15px;
      position: relative;
      width: 100%;
    }
    .boutique__signs-line {
      height: 2px;
      width: 100%;
      background: #7c7575;
      position: relative;
    }
    .boutique__signs-line::before, .boutique__signs-line::after {
      content: "";
      position: absolute;
      top: 50%;
      width: 7px;
      height: 7px;
      background: #555353;
      transform: translateY(-50%);
    }
    .boutique__signs-line::before {
      left: -7px;
    }
    .boutique__signs-line::after {
      right: -7px;
    }
    .boutique__signs-text {
      width: 100%;
      display: flex;
      white-space: nowrap;
      justify-content: space-between;
      color: #333;
      font-size: 20px;
    }
    .boutique__social {
      width: 100%;
      font-size: 20px;
      display: flex;
      gap: 20px;
      justify-content: space-between;
    }
    .boutique__social a {
      color: #333;
      text-decoration: none;
      display: block;
    }
    .boutique__social-icons {
      width: 100%;
      display: flex;
      gap: 10px;
      justify-content: end;
      padding: 0;
      margin: 0;
    }
    .boutique__icon {
      width: 20px;
    }
    .boutique__icon i {
      margin-right: 6px;
    }
  `;

  const convertImage = async (url) => {
    await utils.convertSignedUrl(url)
      .then((response) => {
        setDisable(false);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertBase64 = async () => {
    try {
      // Convert to base64
      const dataUrl = await toPng(htmlRef.current, { cacheBust: true });
      setBase64(dataUrl); // Set the base64 data
      convertImage(dataUrl); // Your function to handle the base64 data
    } catch (error) {
      dispatch(setErrorNotification(error?.response?.data));
    }
  };

  const convertHtml = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link 
              href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Urbanist:wght@100..900&display=swap" 
              rel="stylesheet" />
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => (setData(res)));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data) {
      /* used settimeout the page was fully rendered and then called the api so wait for the response */
      setTimeout(() => {
        convertBase64();
      }, 1000);
      convertHtml();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: <TemplateContentUpload
        base64={base64}
        setNotification={setNotification}
        fileType={fileType}
        htmlContent={htmlContent}
      />,
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: <FileFormat
        setNotification={setNotification}
        handleFileType={handleFileType}
        setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
        data={data}
      />,
    });
  };

  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;
  const backgroundColor = data?.[0]?.styles?.background;
  const backgroundImage = Array.isArray(data?.[0]?.template_backgroundImage_url)
    ? data?.[0]?.template_backgroundImage_url
    : [];

  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />

      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: <PopupBox
            setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
            setNotification={setNotification}
          />,
        }}
      />

      <div className="header-height" />

      <div ref={htmlRef} className="boutique">
        <div style={{ backgroundColor: `${backgroundColor}` }} className="main-container">
          {/* <div className="boutique__parent-logo">
            <img src={backgroundImage[0]} className="boutique__logo" alt="Logo" />
          </div> */}

          <div className="boutique__content">
            <div className="boutique__header">
              <div className="boutique__title">
                <span className="boutique__title-your">{contentDetails?.[0]?.heading?.split(' ')[0]}</span>
                <span className="boutique__title-name">{contentDetails?.[0]?.heading?.split(' ')[1]}</span>
              </div>
            </div>

            {/* Image + Discount */}
            <div className="boutique__image-container">
              <div className="boutique__arch">
                <img src={contentDetails?.[0]?.image} alt="Model" />
              </div>

              <div className="boutique__discount-badge">
                <span className="discount-firstText">up to</span>
                <span className="discount-offer">{contentDetails?.[0]?.offer_percentage}</span>
                <span className="discount-lastText">off</span>
              </div>

              <p className="boutique__special-offer">{contentDetails?.[0]?.description}</p>
            </div>

            {/* Astrological Signs */}
            <div className="boutique__signs">
              <div className="boutique__signs-line"> </div>
              <div className="boutique__signs-text">
                <span>{contentDetails?.[0]?.item_name}</span>
                <span>{contentDetails?.[0]?.item_center}</span>
                <span>{contentDetails?.[0]?.item_right}</span>
              </div>
              <div className="boutique__signs-line"> </div>
            </div>

            {/* Social Media */}
            <div className="boutique__social">
              <span> {contentDetails?.[0]?.website} </span>
              <div className="boutique__social-icons">
                <img className="boutique__icon" alt="" src={backgroundImage[1]} />
                <img className="boutique__icon" alt="" src={backgroundImage[2]} />
                <img className="boutique__icon" alt="" src={backgroundImage[3]} />
              </div>
              <span> {contentDetails?.[0]?.name} </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

HtmlTemplateEightteen.propTypes = {
  templateData:
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      template_title: PropTypes.string.isRequired,
    }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateEightteen;
